import {
  AddressField,
  AllergiesField,
  AppointmentBookingField,
  BelugaPatientPreferenceField,
  BridgeEligibilityField,
  CandidEligibilityField,
  ConditionsField,
  DatabaseSelectField,
  DateField,
  DateStringField,
  DropdownField,
  EmailField,
  FileField,
  HeightField,
  InputTableField,
  InsuranceField,
  MedicationsField,
  MultipleChoiceField,
  NumberField,
  PharmacySearchField,
  PhoneField,
  RankingField,
  RatingField,
  RedirectField,
  RelatedContactsField,
  RichTextField,
  SignatureField,
  StringField,
  StringLongField,
  StripeField,
  ChargebeeField,
  TimeField,
  TimezoneField,
} from "./fields";
import { resolveFileFieldLimits } from "./fields/FileField/resolveFileFieldLimits";
import type { FormTypesWithValues, TellescopeFormFieldPropsMap } from "./types";

export type TellescopeFormFieldProps<T extends FormTypesWithValues> = TellescopeFormFieldPropsMap[T];
type Rest<T extends FormTypesWithValues> = Omit<
  TellescopeFormFieldProps<T>,
  "type" | "value" | "onChange" | "className"
>;

export const TellescopeFormField = <T extends FormTypesWithValues>({
  type,
  value,
  onChange,
  className,
  ...rest
}: TellescopeFormFieldProps<T>) => {
  switch (type) {
    case "Address":
      return <AddressField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Allergies":
      return <AllergiesField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Appointment Booking": {
      const typedRest = rest as unknown as Rest<"Appointment Booking">;
      return (
        <AppointmentBookingField type={type} value={value} onChange={onChange} className={className} {...typedRest} />
      );
    }
    case "Beluga Patient Preference":
      return (
        <BelugaPatientPreferenceField type={type} value={value} onChange={onChange} className={className} {...rest} />
      );
    case "Conditions":
      return <ConditionsField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Database Select":
      return <DatabaseSelectField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "date":
      return <DateField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "dateString":
      return <DateStringField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Dropdown":
      return <DropdownField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "email":
      return <EmailField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "file": {
      const typedRest = rest as unknown as Rest<"file">;
      const validFileTypes = typedRest.options?.validFileTypes;
      const { multiple, maxFiles, maxSizeMB } = resolveFileFieldLimits(typedRest.options, 1);

      return (
        <FileField
          type="files"
          multiple={multiple}
          value={value === undefined ? [] : [value]}
          onChange={(file) => onChange(file?.[0])}
          className={className}
          validFileTypes={validFileTypes?.join(",")}
          maxFiles={maxFiles}
          maxSizeMB={maxSizeMB}
          autoUploadFiles={typedRest.options?.autoUploadFiles}
          hideFromPortal={typedRest.options?.hideFromPortal}
          {...typedRest}
        />
      );
    }

    case "files": {
      const typedRest = rest as unknown as Rest<"files">;
      const validFileTypes = typedRest.options?.validFileTypes;
      const { multiple, maxFiles, maxSizeMB } = resolveFileFieldLimits(typedRest.options, Infinity);

      return (
        <FileField
          multiple={multiple}
          type={type}
          value={value}
          onChange={onChange}
          className={className}
          validFileTypes={validFileTypes?.join(",")}
          maxFiles={maxFiles}
          maxSizeMB={maxSizeMB}
          autoUploadFiles={typedRest.options?.autoUploadFiles}
          hideFromPortal={typedRest.options?.hideFromPortal}
          {...typedRest}
        />
      );
    }
    case "Height":
      return <HeightField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Hidden Value":
      return null;
    case "Input Table":
      return <InputTableField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Insurance":
      return <InsuranceField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Medications":
      return <MedicationsField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "multiple_choice":
      return <MultipleChoiceField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "number":
      return <NumberField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "phone":
      return <PhoneField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Redirect":
      return <RedirectField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "signature": {
      const typedRest = rest as unknown as Rest<"signature">;
      return <SignatureField type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "string":
      return <StringField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "stringLong":
      return <StringLongField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Timezone":
      return (
        <TimezoneField
          type={type}
          value={value}
          onChange={onChange}
          className={className}
          allowInternational={false}
          {...rest}
        />
      );
    case "ranking":
      return <RankingField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "rating":
      return <RatingField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Related Contacts": {
      const typedRest = rest as unknown as Rest<"Related Contacts">;
      return (
        <RelatedContactsField type={type} value={value} onChange={onChange} className={className} {...typedRest} />
      );
    }
    case "Rich Text":
      return <RichTextField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Stripe": {
      const typedRest = rest as unknown as Rest<"Stripe">;
      return <StripeField type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "Chargebee": {
      const typedRest = rest as unknown as Rest<"Chargebee">;
      return <ChargebeeField type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "Time":
      return <TimeField type={type} value={value} onChange={onChange} className={className} {...rest} />;
    case "Bridge Eligibility": {
      const typedRest = rest as unknown as Rest<"Bridge Eligibility">;
      return (
        <BridgeEligibilityField type={type} value={value} onChange={onChange} className={className} {...typedRest} />
      );
    }
    case "Pharmacy Search": {
      const typedRest = rest as unknown as Rest<"Pharmacy Search">;
      return <PharmacySearchField type={type} value={value} onChange={onChange} className={className} {...typedRest} />;
    }
    case "Candid Eligibility": {
      const typedRest = rest as unknown as Rest<"Candid Eligibility">;
      return (
        <CandidEligibilityField type={type} value={value} onChange={onChange} className={className} {...typedRest} />
      );
    }
    default: {
      const _exhaustiveType: undefined | never = type;
      return null;
    }
  }
};
